// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Bootstraps the app, wires the events, owns no heavy logic. Everything that
 * touches a row happens in the worker; this file never sees the corpus except
 * as the exemplars it renders.
 */

import './styles/main.css';
import { EventLog } from './eventlog.ts';
import { createModal, closeAnyModal } from './modal.ts';
import { installGlossary, term } from './glossary.ts';
import { ABOUT, HOW_IT_WORKS, PRIVACY } from './content.ts';
import { MAX_ROWS, type AnalysisResult, type ClusterView, type ExportKind, type LoadedFile, type Role, type WorkerIn, type WorkerOut } from './types.ts';

const app = document.getElementById('app') as HTMLElement;

app.innerHTML = `
  <header class="site-header">
    <div class="brand">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true">
        <circle cx="7" cy="8" r="3" /><circle cx="17" cy="7" r="2.4" /><circle cx="8" cy="17.5" r="2.4" />
        <circle cx="17.5" cy="16.5" r="3.2" stroke-dasharray="2.6 2.4" />
      </svg>
      <span>topicgap<small>the topics your eval set never covered</small></span>
    </div>
    <nav class="header-nav">
      <button type="button" class="nav-button" id="nav-how">How it works</button>
      <button type="button" class="nav-button" id="nav-privacy">Privacy</button>
      <button type="button" class="nav-button" id="nav-about">About</button>
      <button type="button" class="icon-button log-toggle" id="nav-log" aria-expanded="false" aria-controls="event-drawer" aria-label="Open the event log">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h10"/></svg>
        <span class="badge" id="log-badge" hidden></span>
      </button>
    </nav>
  </header>

  <main class="main-content">
    <button type="button" class="trust-banner" id="trust-banner">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v6c0 4.4-3 8.1-7 9-4-.9-7-4.6-7-9V6l7-3z"/></svg>
      <span>Runs entirely in your browser. Your tickets and queries never leave this device — read what is and is not protected.</span>
    </button>

    <div class="hero">
      <h1>Which topics does your eval set never touch?</h1>
      <p>
        Drop in your real support tickets or logged production queries, then drop in the golden set
        somebody wrote in an afternoon. topicgap groups the first, names every group without a
        language model, and tells you what share of your actual traffic the second one never tests.
      </p>
    </div>

    <div class="error-panel" id="error-panel" hidden>
      <h2 id="error-title">Something went wrong</h2>
      <p id="error-message"></p>
      <button type="button" class="secondary-button" id="error-retry">Try again</button>
    </div>

    <section class="step">
      <div class="step-head">
        <span class="step-number">1</span>
        <h2>Your corpus</h2>
      </div>
      <div class="dropzone" id="drop-corpus" tabindex="0" role="button" aria-label="Choose your corpus file">
        <span class="dropzone-title" id="corpus-title">Drop a CSV, TSV or JSONL</span>
        <span class="dropzone-sub" id="corpus-sub">Your tickets, queries or feedback — up to ${MAX_ROWS.toLocaleString()} rows</span>
        <button type="button" class="sample-pill" id="sample-corpus">Try a sample</button>
      </div>
      <div class="column-picker" id="picker-corpus" hidden>
        <label for="column-corpus">Which column holds the text?</label>
        <select id="column-corpus"></select>
        <div class="column-preview" id="preview-corpus"></div>
      </div>
    </section>

    <section class="step" id="step-eval" hidden>
      <div class="step-head">
        <span class="step-number">2</span>
        <h2>Your eval set</h2>
        <span class="optional">optional — but it is the point</span>
      </div>
      <div class="dropzone" id="drop-eval" tabindex="0" role="button" aria-label="Choose your eval set file">
        <span class="dropzone-title" id="eval-title">Drop your golden set or test questions</span>
        <span class="dropzone-sub" id="eval-sub">Without it you get topics; with it you get the gap</span>
        <button type="button" class="sample-pill" id="sample-eval">Try a sample</button>
      </div>
      <div class="column-picker" id="picker-eval" hidden>
        <label for="column-eval">Which column holds the text?</label>
        <select id="column-eval"></select>
        <div class="column-preview" id="preview-eval"></div>
      </div>
    </section>

    <section class="step" id="step-run" hidden>
      <div class="step-head">
        <span class="step-number">3</span>
        <h2>Cluster</h2>
      </div>
      <div class="controls">
        <div class="control">
          <label for="k-range">Number of clusters (<span class="term" data-term="k-means">k</span>)<output id="k-value">12</output></label>
          <input type="range" id="k-range" min="2" max="40" step="1" value="12" />
        </div>
        <div class="control" style="flex: 0 0 auto">
          <button type="button" class="secondary-button" id="suggest-k">Suggest k</button>
        </div>
        <div class="control" style="flex: 0 0 auto">
          <button type="button" class="primary-button" id="run">Find the gap</button>
        </div>
      </div>
      <p class="hint" id="run-hint">
        k is a setting you choose, not something the data announces. Try a few — the exemplars under
        each cluster are how you tell whether the number you picked is describing anything real.
      </p>
      <div class="progress" id="progress" hidden>
        <div class="progress-label"><span id="progress-stage">Working</span><span id="progress-detail"></span></div>
        <div class="progress-track"><div class="progress-fill" id="progress-fill"></div></div>
      </div>
    </section>

    <section id="results" hidden>
      <div class="headline" id="headline"></div>
      <div class="results-head">
        <h2 id="results-title">Clusters</h2>
        <div class="exports">
          <button type="button" class="secondary-button" data-export="labelled">labelled.csv</button>
          <button type="button" class="secondary-button" data-export="topics">topics.md</button>
          <button type="button" class="secondary-button" data-export="sample">sample.jsonl</button>
        </div>
      </div>
      <div id="cluster-list"></div>
    </section>
  </main>

  <footer class="site-footer">
    <div class="footer-inner">
      <span>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></span>
    </div>
  </footer>
`;

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const logToggle = $<HTMLButtonElement>('nav-log');
const eventLog = new EventLog(document.body, logToggle, $('log-badge'));
installGlossary();

const howModal = createModal('modal-how', 'How it works', HOW_IT_WORKS);
const privacyModal = createModal('modal-privacy', 'Privacy &amp; threat model', PRIVACY);
const aboutModal = createModal('modal-about', 'About topicgap', ABOUT);

$('nav-how').addEventListener('click', () => howModal.open());
$('nav-privacy').addEventListener('click', () => privacyModal.open());
$('nav-about').addEventListener('click', () => aboutModal.open());
$('trust-banner').addEventListener('click', () => privacyModal.open());

// ── State ────────────────────────────────────────────────────────────────────

interface FileState {
  file: LoadedFile;
  column: number;
}

const state: { corpus: FileState | null; evalSet: FileState | null; result: AnalysisResult | null; busy: boolean } = {
  corpus: null,
  evalSet: null,
  result: null,
  busy: false,
};

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
const send = (message: WorkerIn): void => worker.postMessage(message);

// ── Progress and errors ──────────────────────────────────────────────────────

const progress = $('progress');
const progressStage = $('progress-stage');
const progressDetail = $('progress-detail');
const progressFill = $('progress-fill');

function showProgress(stage: string, done: number, total: number, detail = ''): void {
  progress.hidden = false;
  progressStage.textContent = stage;
  progressDetail.textContent = detail;
  const fraction = total > 0 ? Math.min(1, done / total) : 0;
  progressFill.style.width = `${(fraction * 100).toFixed(1)}%`;
}

function hideProgress(): void {
  progress.hidden = true;
}

const errorPanel = $('error-panel');
function showError(title: string, message: string): void {
  $('error-title').textContent = title;
  $('error-message').textContent = message;
  errorPanel.hidden = false;
  errorPanel.scrollIntoView({ block: 'nearest' });
}
$('error-retry').addEventListener('click', () => {
  errorPanel.hidden = true;
  if (state.corpus) void run();
});

// ── File ingestion ───────────────────────────────────────────────────────────

const ACCEPT = '.csv,.tsv,.txt,.jsonl,.ndjson,text/csv,text/plain,application/json';

function pickFile(role: Role): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = ACCEPT;
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) loadFile(role, file);
  });
  input.click();
}

/**
 * The ONE ingestion path. The drop zone, the file picker, a paste and the
 * "Try a sample" pill all arrive here — a sample that took a different route
 * would demonstrate a code path no real user exercises.
 */
function loadFile(role: Role, file: File): void {
  errorPanel.hidden = true;
  eventLog.add('info', `Reading ${file.name} (${formatBytes(file.size)}) — in this tab, not uploaded.`);
  send({ type: 'profile', role, blob: file, name: file.name });
}

async function loadSample(role: Role): Promise<void> {
  const name = role === 'corpus' ? 'support-queries.csv' : 'golden-set.csv';
  try {
    const response = await fetch(`${import.meta.env.BASE_URL}samples/${name}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    loadFile(role, new File([blob], name, { type: 'text/csv' }));
  } catch (error) {
    showError(
      'The sample could not be loaded',
      `${error instanceof Error ? error.message : String(error)} — drop your own CSV instead, which is the path this tool actually exists for.`,
    );
  }
}

function wireDropzone(role: Role, zone: HTMLElement, sampleButton: HTMLElement): void {
  zone.addEventListener('click', () => pickFile(role));
  zone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      pickFile(role);
    }
  });
  // stopPropagation, or the pill also opens the file picker behind the sample.
  sampleButton.addEventListener('click', (event) => {
    event.stopPropagation();
    void loadSample(role);
  });

  for (const type of ['dragenter', 'dragover'] as const) {
    zone.addEventListener(type, (event) => {
      event.preventDefault();
      zone.classList.add('dragging');
    });
  }
  for (const type of ['dragleave', 'drop'] as const) {
    zone.addEventListener(type, (event) => {
      event.preventDefault();
      zone.classList.remove('dragging');
    });
  }
  zone.addEventListener('drop', (event) => {
    const file = (event as DragEvent).dataTransfer?.files?.[0];
    if (file) loadFile(role, file);
  });
}

wireDropzone('corpus', $('drop-corpus'), $('sample-corpus'));
wireDropzone('eval', $('drop-eval'), $('sample-eval'));

document.addEventListener('paste', (event) => {
  const file = event.clipboardData?.files?.[0];
  if (file) {
    event.preventDefault();
    loadFile(state.corpus ? 'eval' : 'corpus', file);
  }
});

// ── The column picker ────────────────────────────────────────────────────────

function renderPicker(role: Role, file: LoadedFile): void {
  const suffix = role === 'corpus' ? 'corpus' : 'eval';
  const picker = $(`picker-${suffix}`);
  const select = $<HTMLSelectElement>(`column-${suffix}`);
  const preview = $(`preview-${suffix}`);
  const zone = $(`drop-${suffix}`);

  zone.classList.add('loaded');
  zone.innerHTML = '';
  const summary = document.createElement('div');
  summary.className = 'file-summary';
  const strong = document.createElement('strong');
  // textContent for the filename — it is the one user-controlled string this
  // page renders, and it must never be parsed as markup.
  strong.textContent = file.name;
  summary.append(strong);
  const meta = document.createElement('span');
  meta.textContent = `${file.format.toUpperCase()} · ${file.headers.length} columns · ${
    file.truncated
      ? `${file.totalRows.toLocaleString()} rows, first ${MAX_ROWS.toLocaleString()} used`
      : `${file.totalRows.toLocaleString()} rows`
  }`;
  summary.append(meta);
  const change = document.createElement('button');
  change.type = 'button';
  change.className = 'sample-pill';
  change.textContent = 'Change file';
  change.addEventListener('click', (event) => {
    event.stopPropagation();
    pickFile(role);
  });
  summary.append(change);
  zone.append(summary);

  select.innerHTML = '';
  for (const column of file.columns) {
    const option = document.createElement('option');
    option.value = String(column.index);
    option.textContent = `${column.name} — ${column.meanWords.toFixed(1)} words avg, ${column.distinct.toLocaleString()} distinct`;
    select.append(option);
  }
  select.value = String(file.proposedColumn);
  picker.hidden = false;

  const paint = (): void => {
    const index = Number(select.value);
    const column = file.columns[index];
    preview.innerHTML = '';
    for (const sample of column?.samples ?? []) {
      const row = document.createElement('div');
      row.textContent = sample;
      preview.append(row);
    }
    if ((column?.samples.length ?? 0) === 0) {
      const row = document.createElement('div');
      row.textContent = '(this column is empty in the sampled rows)';
      preview.append(row);
    }
    const target = role === 'corpus' ? state.corpus : state.evalSet;
    if (target) target.column = index;
  };
  select.onchange = paint;
  paint();
}

// ── Rendering the result ─────────────────────────────────────────────────────

const pct = (x: number): string => `${(x * 100).toFixed(1)}%`;

function renderHeadline(result: AnalysisResult): void {
  const h = result.headline;
  const headline = $('headline');
  const hasEval = h.evalRows > 0;

  if (!hasEval) {
    headline.innerHTML = `
      <div class="headline-number">${h.k} clusters</div>
      <p class="headline-claim">
        ${h.corpusRows.toLocaleString()} rows grouped into ${h.k} clusters. Load an eval set above and
        topicgap will tell you what share of this traffic it never tests — which is the question this
        tool exists for.
      </p>
      <p class="caveat">
        These are the clusters at <code>k = ${h.k}</code>. They are not "your real topics" — k is a
        setting you chose, and a different k gives a different set.
      </p>`;
    return;
  }

  const lift = h.uncoveredVolume - h.chanceUncoveredVolume;
  // How many of the uncovered clusters are, on their own, too small for this
  // eval set to have demonstrated anything about.
  const smallUncovered = result.clusters.filter((c) => !c.covered && c.share < h.detectionFloor).length;
  headline.innerHTML = `
    <div class="headline-number">${pct(h.uncoveredVolume)}</div>
    <p class="headline-claim">
      of your traffic sits in ${h.uncoveredClusters} cluster${h.uncoveredClusters === 1 ? '' : 's'}
      that your ${h.evalRows.toLocaleString()}-row eval set never covers.
    </p>
    <dl class="headline-facts">
      <div class="fact"><dt>${term('Chance level', 'chance level')}</dt><dd>${pct(h.chanceUncoveredVolume)}</dd></div>
      <div class="fact"><dt>Above chance by</dt><dd>${lift >= 0 ? '+' : ''}${pct(lift)}</dd></div>
      <div class="fact"><dt>Uncovered clusters</dt><dd>${h.uncoveredClusters} of ${h.k} (chance ${h.chanceUncoveredClusters.toFixed(1)})</dd></div>
      <div class="fact"><dt>${term('Detection floor', 'detection floor')}</dt><dd>${pct(h.detectionFloor)}</dd></div>
    </dl>
    <p class="caveat">
      The finding is the <em>difference</em> between ${pct(h.uncoveredVolume)} and the
      ${pct(h.chanceUncoveredVolume)} an eval set of the same size drawn at random from your own
      corpus would have left uncovered.
      ${
        smallUncovered > 0
          ? `<br /><strong>${smallUncovered} of the ${h.uncoveredClusters} uncovered clusters are individually smaller than the ${pct(h.detectionFloor)} detection floor</strong>, so no one of them proves a gap on its own — ${h.evalRows} eval rows could plausibly have missed any single one by luck. Their <em>combined</em> ${pct(h.uncoveredVolume)} against a ${pct(h.chanceUncoveredVolume)} chance level is what carries the finding, which is exactly why the headline is a volume and not a list of topics.`
          : ''
      }
      ${
        h.outOfRangeEvalRows > 0
          ? `<br /><strong>${h.outOfRangeEvalRows} eval row${h.outOfRangeEvalRows === 1 ? '' : 's'}</strong> landed outside every cluster's ${term('radius')} — they are testing something this corpus does not contain.`
          : ''
      }
      ${h.emptyRows > 0 ? `<br />${h.emptyRows.toLocaleString()} rows had no usable text and were not clustered; they still appear in <code>labelled.csv</code>.` : ''}
    </p>`;
}

function renderClusters(result: AnalysisResult): void {
  const list = $('cluster-list');
  list.innerHTML = '';
  const hasEval = result.headline.evalRows > 0;
  const floor = result.headline.detectionFloor;

  const ordered = [...result.clusters].sort((a, b) => {
    if (hasEval && a.covered !== b.covered) return a.covered ? 1 : -1;
    return b.size - a.size;
  });

  for (const cluster of ordered) list.append(renderCluster(cluster, hasEval, floor));

  $('results-title').textContent = hasEval
    ? `${result.headline.uncoveredClusters} uncovered, then the rest`
    : `${result.headline.k} clusters`;
}

function renderCluster(cluster: ClusterView, hasEval: boolean, floor: number): HTMLElement {
  const root = document.createElement('article');
  root.className = `cluster ${hasEval ? (cluster.covered ? 'covered' : 'uncovered') : ''}`;

  const head = document.createElement('button');
  head.type = 'button';
  head.className = 'cluster-head';
  head.setAttribute('aria-expanded', 'false');

  const label = document.createElement('span');
  label.className = 'cluster-label';
  label.textContent = cluster.renamed ?? cluster.label;
  head.append(label);

  if (hasEval) {
    const pill = document.createElement('span');
    if (cluster.covered) {
      pill.className = 'pill covered';
      pill.textContent = `covered ×${cluster.coveringCount}`;
    } else {
      pill.className = 'pill uncovered';
      pill.textContent = 'uncovered';
    }
    head.append(pill);

    // "Uncovered" is a FACT about this cluster; "below the floor" is a separate
    // fact about how much that one cluster can prove on its own. An earlier
    // version replaced the first with the second, which hid the finding behind
    // its own caveat — and, because small clusters are exactly the ones a thin
    // eval set misses, it hid it on almost every cluster that mattered.
    if (!cluster.covered && cluster.share < floor) {
      const note = document.createElement('span');
      note.className = 'pill floor';
      note.textContent = 'below floor on its own';
      note.title = `At ${pct(cluster.share)} of traffic this cluster is smaller than the ${pct(floor)} detection floor, so on its own it could have been missed by chance. The headline aggregates it with the others.`;
      head.append(note);
    }
  }

  const meta = document.createElement('span');
  meta.className = 'cluster-meta';
  meta.textContent = `${cluster.size.toLocaleString()} · ${pct(cluster.share)}`;
  head.append(meta);
  root.append(head);

  const body = document.createElement('div');
  body.className = 'cluster-body';
  body.hidden = true;

  const stats = document.createElement('div');
  stats.className = 'cluster-stats';
  stats.innerHTML = `
    <div class="fact"><dt>${term('Cohesion', 'cohesion')}</dt><dd>${cluster.cohesion.toFixed(3)}</dd></div>
    <div class="fact"><dt>${term('Ambiguous', 'ambiguous')} members</dt><dd>${pct(cluster.ambiguous)}</dd></div>
    <div class="fact"><dt>${term('Radius')}</dt><dd>${cluster.radius.toFixed(3)}</dd></div>
    ${
      hasEval
        ? `<div class="fact"><dt>Nearest eval rows</dt><dd>${cluster.nearestCount} (best ${cluster.bestEvalSimilarity < 0 ? 'n/a' : cluster.bestEvalSimilarity.toFixed(3)})</dd></div>`
        : ''
    }`;
  body.append(stats);

  const terms = document.createElement('div');
  terms.className = 'terms';
  for (const t of cluster.terms.slice(0, 8)) {
    const span = document.createElement('span');
    span.textContent = t.term;
    terms.append(span);
  }
  body.append(terms);

  // The exemplars are mandatory rather than decorative: cutting the map removed
  // the only other way to falsify these numbers.
  for (const exemplar of cluster.exemplars) {
    const quote = document.createElement('blockquote');
    quote.className = 'exemplar';
    quote.textContent = exemplar.text.replace(/\s+/g, ' ').trim().slice(0, 280);
    body.append(quote);
  }

  const rename = document.createElement('div');
  rename.className = 'rename-row';
  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'Rename this cluster — the name flows into every export';
  input.value = cluster.renamed ?? '';
  input.setAttribute('aria-label', `Rename cluster ${cluster.id}`);
  const save = document.createElement('button');
  save.type = 'button';
  save.className = 'secondary-button';
  save.textContent = 'Rename';
  const commit = (): void => {
    const name = input.value.trim();
    send({ type: 'rename', cluster: cluster.id, name: name === '' ? null : name });
    eventLog.add('info', `Cluster ${cluster.id} renamed.`);
  };
  save.addEventListener('click', commit);
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      commit();
    }
  });
  rename.append(input, save);
  body.append(rename);

  root.append(body);
  head.addEventListener('click', () => {
    body.hidden = !body.hidden;
    head.setAttribute('aria-expanded', String(!body.hidden));
  });
  return root;
}

// ── Running ──────────────────────────────────────────────────────────────────

const runButton = $<HTMLButtonElement>('run');
const kRange = $<HTMLInputElement>('k-range');
const kValue = $('k-value');

kRange.addEventListener('input', () => {
  kValue.textContent = kRange.value;
});

function run(): void {
  if (!state.corpus || state.busy) return;
  state.busy = true;
  runButton.disabled = true;
  errorPanel.hidden = true;
  send({
    type: 'analyse',
    corpusColumn: state.corpus.column,
    evalColumn: state.evalSet ? state.evalSet.column : null,
    k: Number(kRange.value),
    seed: 0x5eed,
  });
}

runButton.addEventListener('click', run);
$('suggest-k').addEventListener('click', () => {
  if (!state.corpus) return;
  eventLog.add('info', 'Fitting the cohesion curve to suggest k.');
  send({ type: 'suggest-k' });
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'BUTTON') return;
    if (!runButton.disabled && state.corpus) {
      event.preventDefault();
      run();
    }
  }
});

// ── Exports ──────────────────────────────────────────────────────────────────

for (const button of document.querySelectorAll<HTMLButtonElement>('[data-export]')) {
  button.addEventListener('click', () => {
    if (!state.result) return;
    send({ type: 'export', kind: button.dataset.export as ExportKind });
  });
}

async function deliver(blob: Blob, filename: string): Promise<void> {
  const file = new File([blob], filename, { type: blob.type });
  // Web Share first where the platform can take a file — on a phone the whole
  // point is getting this into Slack or an email, not into a downloads folder.
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename });
      eventLog.add('good', `${filename} shared.`);
      return;
    } catch (error) {
      // A user cancelling the share sheet is not a failure; fall through.
      if ((error as DOMException)?.name === 'AbortError') return;
    }
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  eventLog.add('good', `${filename} downloaded (${formatBytes(blob.size)}).`);
}

// ── Worker messages ──────────────────────────────────────────────────────────

worker.addEventListener('message', (event: MessageEvent<WorkerOut>) => {
  const message = event.data;
  switch (message.type) {
    case 'log':
      eventLog.add(message.level, message.message);
      break;

    case 'model-progress':
      if (message.fromCache) {
        showProgress('Model ready from cache', 1, 1);
      } else {
        showProgress(
          `Downloading the ${message.label}`,
          message.received,
          message.total,
          `${formatBytes(message.received)} / ${formatBytes(message.total)}`,
        );
      }
      break;

    case 'model-ready':
      hideProgress();
      break;

    case 'profiled': {
      const entry: FileState = { file: message.file, column: message.file.proposedColumn };
      if (message.file.role === 'corpus') {
        state.corpus = entry;
        $('step-eval').hidden = false;
        $('step-run').hidden = false;
      } else {
        state.evalSet = entry;
      }
      renderPicker(message.file.role, message.file);
      hideProgress();
      // The model download starts as soon as there is a file, so the fetch
      // overlaps the time the user spends checking the column.
      send({ type: 'load-model' });
      break;
    }

    case 'progress':
      showProgress(
        message.stage,
        message.done,
        message.total,
        message.rate ? `${Math.round(message.rate).toLocaleString()} rows/s` : '',
      );
      break;

    case 'suggested-k':
      kRange.value = String(message.k);
      kValue.textContent = String(message.k);
      $('run-hint').textContent = `Suggested k = ${message.k}, from the elbow of the cohesion curve fitted on ${message.fittedOn.toLocaleString()} rows. It is a starting point, not an answer — read the exemplars.`;
      break;

    case 'result':
      state.result = message.result;
      state.busy = false;
      runButton.disabled = false;
      hideProgress();
      $('results').hidden = false;
      renderHeadline(message.result);
      renderClusters(message.result);
      break;

    case 'export':
      void deliver(message.blob, message.filename);
      break;

    case 'error':
      state.busy = false;
      runButton.disabled = false;
      hideProgress();
      eventLog.add('bad', `${message.stage}: ${message.message}`);
      showError('That did not work', message.message);
      break;
  }
});

worker.addEventListener('error', (event) => {
  state.busy = false;
  runButton.disabled = false;
  hideProgress();
  showError(
    'The worker stopped',
    event.message || 'The background worker failed to start. Reload the page and try again.',
  );
});

// ── Utilities ────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

eventLog.add('good', 'topicgap ready. Nothing is uploaded — the only network request is the model, once.');

// Exposed for the deployment dry-run: it drives the same code paths a user does,
// so a green check here is a check of the real thing rather than of a test hook.
interface TopicgapTestHook {
  loadSample: (role: Role) => Promise<void>;
  run: () => void;
  getState: () => { corpusRows: number | null; evalRows: number | null; clusters: number | null; uncoveredVolume: number | null };
  exportKind: (kind: ExportKind) => void;
}
(window as unknown as { topicgap: TopicgapTestHook }).topicgap = {
  loadSample,
  run,
  getState: () => ({
    corpusRows: state.corpus?.file.totalRows ?? null,
    evalRows: state.evalSet?.file.totalRows ?? null,
    clusters: state.result?.clusters.length ?? null,
    uncoveredVolume: state.result?.headline.uncoveredVolume ?? null,
  }),
  exportKind: (kind) => send({ type: 'export', kind }),
};

export { closeAnyModal };
